import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import type {SimpleKeyboard} from 'react-simple-keyboard';
import deepmerge from 'deepmerge';
import type {VirtualKeyboardConfig} from './types';
import {DEFAULT_VIRTUAL_KEYBOARD_OPTIONS} from './constants';
import {VirtualKeyboardPortal} from 'components/common/VirtualKeyboard';
import {useTimeoutRef} from '@chekin/core';

const FOCUS_TIMEOUT = 100;

type Config = Partial<VirtualKeyboardConfig> & {fields: VirtualKeyboardConfig['fields']};

export function useVirtualKeyboard(config: Config) {
  const mergedOptions = useMemo(() => {
    return deepmerge(DEFAULT_VIRTUAL_KEYBOARD_OPTIONS, config.options || {});
  }, [config.options]);

  const mergedConfig = useMemo(
    () => ({
      fields: config.fields,
      options: mergedOptions,
    }),
    [config.fields, mergedOptions],
  );

  const [activeField, setActiveField] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const keyboardRef = useRef<SimpleKeyboard | null>(null);
  const [layout, setLayout] = useState<'default' | 'shift' | 'alt'>('default');
  const [isVisible, setIsVisible] = useState(
    mergedConfig.options?.defaultVisible ?? false,
  );
  const focusTimeoutRef = useTimeoutRef();

  const handleShift = useCallback(() => {
    setLayout(layout === 'default' ? 'shift' : 'default');
  }, [layout]);

  const handleAlt = useCallback(() => {
    setLayout(layout === 'default' ? 'alt' : 'default');
  }, [layout]);

  const handleDefault = useCallback(() => {
    setLayout('default');
  }, []);

  const toggleVisibility = useCallback(() => {
    setIsVisible(prev => !prev);
  }, []);

  const handleChange = useCallback(
    (value: string) => {
      if (!activeField) return;

      const fieldHandler = mergedConfig.fields[activeField];
      if (!fieldHandler) return;

      fieldHandler.setValue(value);
    },
    [activeField, mergedConfig.fields],
  );

  const handleFocus = useCallback(
    (fieldId: string) => {
      setActiveField(fieldId);
      const fieldHandler = mergedConfig.fields[fieldId];
      fieldHandler?.onFocus?.();

      const currentValue = fieldHandler?.getValue() || '';
      keyboardRef.current?.setInput(currentValue);

      if (mergedConfig.options?.autoShowOnFocus !== false) {
        setIsVisible(true);
      }
    },
    [mergedConfig.fields, mergedConfig.options?.autoShowOnFocus],
  );

  const handleBlur = useCallback(
    (fieldId: string) => {
      const fieldHandler = mergedConfig.fields[fieldId];
      fieldHandler?.onBlur?.();

      if (mergedConfig.options?.clearOnBlur) {
        keyboardRef.current?.clearInput();
      }

      if (activeField === fieldId && mergedConfig.options?.autoHideOnBlur) {
        setActiveField(null);
        setIsVisible(false);
      }
    },
    [
      mergedConfig.fields,
      mergedConfig.options?.clearOnBlur,
      mergedConfig.options?.autoHideOnBlur,
      activeField,
    ],
  );

  const handleEnterKey = useCallback(() => {
    if (!activeField) return;

    const currentFieldConfig = mergedConfig.fields[activeField];
    if (!currentFieldConfig) return;

    if (currentFieldConfig.preventEnterNavigation) {
      return;
    }

    if (currentFieldConfig.onEnterKey) {
      currentFieldConfig.onEnterKey();
      return;
    }

    const currentInput = (document.querySelector(
      `[name="${activeField}"], [data-field-id="${activeField}"]`,
    ) || document.activeElement) as HTMLElement;

    if (currentInput?.tagName === 'TEXTAREA') {
      return;
    }

    if (currentFieldConfig.nextFieldId) {
      if (currentFieldConfig.nextFieldId === null) {
        handleBlur(activeField);
        currentInput?.blur();
        setIsVisible(false);
      } else {
        handleFocus(currentFieldConfig.nextFieldId);
        const nextInput = (document.querySelector(
          `[name="${currentFieldConfig.nextFieldId}"], [data-field-id="${currentFieldConfig.nextFieldId}"]`,
        ) || document.activeElement) as HTMLElement;
        nextInput?.focus();
      }
      return;
    }

    const allFields = Array.from(
      document.querySelectorAll('[name], [data-field-id]'),
    ) as HTMLElement[];

    const visibleFields = allFields.filter(field => {
      const tagName = field.tagName;
      return (
        (tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT') &&
        !field.hasAttribute('disabled') &&
        field.offsetParent !== null
      );
    });

    const currentIndex = visibleFields.indexOf(currentInput);
    const nextField = visibleFields[currentIndex + 1];
    if (nextField) {
      const nextFieldId =
        nextField.getAttribute('name') || nextField.getAttribute('data-field-id');
      if (nextFieldId) {
        focusTimeoutRef.current = setTimeout(() => {
          handleFocus(nextFieldId);
          nextField.focus();
        }, FOCUS_TIMEOUT);
        return;
      }
    } else {
      handleBlur(activeField);
      currentInput?.blur();
      setIsVisible(false);
    }
  }, [activeField, mergedConfig.fields, handleFocus, handleBlur, focusTimeoutRef]);

  const handleKeyPress = useCallback(
    (button: string) => {
      switch (button) {
        case '{shift}':
        case '{lock}':
          handleShift();
          break;
        case '{alt}':
          handleAlt();
          break;
        case '{enter}':
          handleEnterKey();
          return;
        case '{default}':
        case '{shiftactivated}':
          handleDefault();
          break;
      }
    },
    [handleShift, handleAlt, handleDefault, handleEnterKey],
  );

  useEffect(
    function preventFocusOnVirtualKeyboard() {
      if (!mergedConfig.options?.preventFocus) return;

      const keyboard = wrapperRef.current;
      if (!keyboard) return;

      const preventFocus = (e: Event) => {
        const target = e.target as HTMLElement;
        if (!target) return;

        let current: HTMLElement | null = target;
        while (current) {
          if (current.hasAttribute('data-exclude-from-prevent-focus')) {
            return;
          }
          current = current.parentElement;
        }

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      };

      const portalContainer = document.getElementById('virtual-keyboard-portal');
      if (portalContainer) {
        portalContainer.addEventListener('mousedown', preventFocus, true);
        portalContainer.addEventListener('mouseup', preventFocus, true);
        portalContainer.addEventListener('touchstart', preventFocus, true);
        portalContainer.addEventListener('touchend', preventFocus, true);
        portalContainer.addEventListener('click', preventFocus, true);
      }

      return () => {
        portalContainer?.removeEventListener('mousedown', preventFocus, true);
        portalContainer?.removeEventListener('mouseup', preventFocus, true);
        portalContainer?.removeEventListener('touchstart', preventFocus, true);
        portalContainer?.removeEventListener('touchend', preventFocus, true);
        portalContainer?.removeEventListener('click', preventFocus, true);
      };
    },
    [mergedConfig.options?.preventFocus, isVisible],
  );

  const clearInput = useCallback(() => {
    keyboardRef.current?.clearInput();
  }, []);

  const keyboard = useMemo(() => {
    return (
      <VirtualKeyboardPortal
        onKeyPress={handleKeyPress}
        onChange={handleChange}
        wrapperRef={wrapperRef}
        keyboardRef={keyboardRef}
        layoutName={layout}
        className={mergedConfig.options?.className}
        isVisible={isVisible}
        onToggle={toggleVisibility}
      />
    );
  }, [
    handleKeyPress,
    handleChange,
    wrapperRef,
    keyboardRef,
    layout,
    mergedConfig.options?.className,
    isVisible,
    toggleVisibility,
  ]);

  return {
    activeField,
    handleFocus,
    handleBlur,
    wrapperRef,
    keyboardRef,
    handleKeyPress,
    clearInput,
    layout,
    keyboard,
    isVisible,
    toggleVisibility,
  };
}
